import React, { useCallback, useEffect, useState } from "react";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Block } from "modules/common/components/page/Block";
import { useLocation, type NavigateFunction } from "react-router";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { ProtectedRoute } from "modules/common/components/routing/ProtectedRoute";
import { routes } from "modules/common/routes";
import { observer } from "mobx-react";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { CategoryDetails } from "./details/CategoryDetails";
import { CategorysList } from "./list/CategoriesList";
import { menuItems } from "modules/root/components/menu/MainMenu";
import { can } from "modules/session/auth/access";
import functionalities from "modules/dictionaries/functionalities";
import { Link } from "react-router";
import styles from "./CategoriesPage.module.scss";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { Search } from "modules/common/components/form/Search";
import { MaterialValuesStoreType } from "../models/material-value-store";
import { ItemsTabsPage } from "./ItemsTabsPage";
import ItemsFreePage from "./MaterialValuesFreePage";
import { MaterialValueDetails } from "./details/MaterialValueDetails";
import { CategoriesStoreType } from "../models/categories-store";
import { MaterialValuesListType } from "../models/material-value-list";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";

const menu = menuItems();

interface CategoriesPageProps {
    access: string[];
    store: MaterialValuesStoreType;
    categories: CategoriesStoreType;
}

const getDeletedMaterials = (store: MaterialValuesListType) => store.deletedMaterials;
const getDeletedMaterialsAll = (store: MaterialValuesListType) => store.deletedMaterialsAll;

const getEndedMaterials = (store: MaterialValuesListType) => store.endedMaterials;
const getEndedMaterialsAll = (store: MaterialValuesListType) => store.endedMaterialsAll;

const getFixedMaterials = (store: MaterialValuesListType) => store.fixedMaterials;

const getFreeMaterials = (store: MaterialValuesListType) => store.freeMaterials;
const getMaterialsAll = (store: MaterialValuesListType) => store.materialsAll;

const openCategory = (navigate: NavigateFunction, id: string) => {
    const url = id ? routes.dictionaries.inventory.categoriesDetails(id) : routes.dictionaries.inventory.path;
    navigate(url);
};

const getSelectedCategoryId = (pathname: string) => {
    const match = matchRoute<any>(pathname, routes.dictionaries.inventory.categoriesDetails());
    return match && match.params ? match.params.id : "";
};

export const CategoriesPage = observer((props: CategoriesPageProps) => {
    const { store, access } = props;
    const navigate = useStableNavigate();
    const { pathname } = useLocation();
    const categoryId = getSelectedCategoryId(pathname);
    const [category, setcategory] = useState("");
    const [filterCategories, setFilterCategories] = useState(true);
    const categories = props.categories;

    useEffect(() => {
        document.title = menu.dictionaries.nested.inventory.label;

        categories.list.load();

        store.employers.load();
        store.update();
    }, [categories, store]);

    // карточка следует за адресом: открыли другую категорию — загружаем её
    useEffect(() => {
        categories.details.load(categoryId);
    }, [categories.details, categoryId]);

    const onCategorySelected = useCallback((id = "") => openCategory(navigate, id), [navigate]);

    const onCategoryClicked = (id = "") => {
        props.store.list.setCategory(id);
        setcategory(id);
    };

    const onCategoryRemoved = useCallback(() => {
        categories.list.load();
        navigate(routes.dictionaries.inventory.path);
    }, [navigate, categories.list]);

    const onCategoryReload = useCallback(() => {
        categories.list.load();
    }, [categories.list]);

    const onCategorySaved = useCallback(() => {
        openCategory(navigate, "");
        categories.list.load();
    }, [navigate, categories.list]);

    const onCategoryAdd = useCallback(() => openCategory(navigate, EMPTY_OBJECT_ID), [navigate]);

    const canChange = can(functionalities.INVENTORY_EDIT, props.access);
    const onMaterialSelected = (id: string = "") => {
        const url = id ? routes.dictionaries.inventory.itemsDetils(id) : routes.dictionaries.inventory.path;

        navigate(url);
    };
    const onMaterialValueAdd = () => {
        onMaterialSelected(EMPTY_OBJECT_ID);
    };
    const onMaterialSaved = (id: string) => {
        onMaterialSelected("");
        store.list.load();
        categories.updateCategory(id);
    };

    const filterAll = () => {
        setFilterCategories(false);
    };

    const filterCategory = () => {
        setFilterCategories(true);
    };

    return (
        <div className={styles.container}>
            <div className={filterCategories ? styles.leftBlock : styles.leftBlockHidden}>
                <Block columned={true} className={styles.page}>
                    <WhenPath path={routes.dictionaries.inventory.categoriesDetails()}>
                        <DetailsPopup title="Категория" onCancel={onCategorySelected}>
                            <CategoryDetails
                                category={categories.details}
                                onSaved={onCategorySaved}
                                onRemoved={onCategoryRemoved}
                                canChange={canChange}
                            />
                        </DetailsPopup>
                    </WhenPath>
                    <span className={styles.topSpan}>Категории</span>
                    <div className={styles.nav}>
                        {canChange && (
                            <PlanrButton
                                type="secondary"
                                size="small"
                                icon="general-plus-big"
                                onClick={onCategoryAdd}
                                round={true}
                                style={{
                                    marginBottom: "32px",
                                }}
                            />
                        )}

                        <Link to={routes.dictionaries.inventory.pathGroups} className="planr-button-link">
                            <PlanrButton
                                type="lightblueish"
                                size="small"
                                style={{
                                    width: "200px",
                                    marginBottom: "32px",
                                    borderRadius: "120px",
                                }}
                            >
                                Группы амортизации
                            </PlanrButton>
                        </Link>
                    </div>
                    <CategorysList
                        list={categories.list}
                        onSelected={onCategorySelected}
                        onClicked={onCategoryClicked}
                        onRemoved={onCategoryRemoved}
                    />
                </Block>
            </div>
            <div className={filterCategories ? styles.rightBlock : styles.rightBlockFull}>
                <span className={styles.topSpan}>Ценности</span>
                <div className={styles.filterBtns}>
                    <PlanrButton
                        type={filterCategories ? "primary" : "graybtn"}
                        size="small"
                        onClick={filterCategory}
                        style={{ borderTopRightRadius: "0px", borderBottomRightRadius: "0px", width: "151px" }}
                    >
                        По категориям
                    </PlanrButton>
                    <PlanrButton
                        type={!filterCategories ? "primary" : "graybtn"}
                        size="small"
                        onClick={filterAll}
                        style={{
                            borderTopLeftRadius: "0px",
                            borderBottomLeftRadius: "0px",
                            marginLeft: "0px",
                            width: "151px",
                        }}
                    >
                        Весь список
                    </PlanrButton>
                </div>
                <WhenPath path={routes.dictionaries.inventory.itemsDetils()}>
                    <DetailsPopup title="Ценность" onCancel={onMaterialSelected} width={650}>
                        <MaterialValueDetails
                            onPrintCredentials={store.printCredentials}
                            canChange={canChange}
                            materialValue={store.details}
                            onSaved={onMaterialSaved}
                            categories={store.categories}
                            deprGroups={store.deprGroups}
                            employers={store.employers}
                            category={category}
                        />
                    </DetailsPopup>
                </WhenPath>
                {canChange && (
                    <PlanrButton
                        type="secondary"
                        size="small"
                        icon="general-plus-big"
                        onClick={onMaterialValueAdd}
                        round={true}
                        style={{
                            position: "absolute",
                            top: "70px",
                            left: "24px",
                            zIndex: 5,
                        }}
                    />
                )}
                <div
                    style={{
                        position: "absolute",
                        top: "70px",
                        right: "42px",
                    }}
                >
                    <Search query={store.list.query} onSearch={store.list.setQuery} />
                </div>

                {filterCategories ? (
                    <ProtectedRoute loggedIn={true}>
                            <ItemsTabsPage
                                itemsFreePanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            category={category}
                                            getDataset={getFreeMaterials}
                                        />
                                    ) : null
                                }
                                itemsFixedPanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            category={category}
                                            getDataset={getFixedMaterials}
                                        />
                                    ) : null
                                }
                                itemsEndedPanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            category={category}
                                            getDataset={getEndedMaterials}
                                            ended={true}
                                        />
                                    ) : null
                                }
                                itemsDeletedPanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            category={category}
                                            getDataset={getDeletedMaterials}
                                        />
                                    ) : null
                                }
                            />
                    </ProtectedRoute>
                ) : (
                    <ProtectedRoute loggedIn={true}>
                            <ItemsTabsPage
                                itemsAllPanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            getDataset={getMaterialsAll}
                                            all={true}
                                        />
                                    ) : null
                                }
                                itemsEndedPanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            category={category}
                                            getDataset={getEndedMaterialsAll}
                                            ended={true}
                                            all={true}
                                        />
                                    ) : null
                                }
                                itemsDeletedPanel={
                                    can(functionalities.INVENTORY_READ, access) ? (
                                        <ItemsFreePage
                                            onChange={onCategoryReload}
                                            store={store}
                                            access={access}
                                            category={category}
                                            getDataset={getDeletedMaterialsAll}
                                            all={true}
                                        />
                                    ) : null
                                }
                            />
                    </ProtectedRoute>
                )}
            </div>
        </div>
    );
});
